import { useEffect, useLayoutEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { usePage, type SectionProps } from "@/flash/context";
import { A, Container, Slot, cn, str } from "@/flash/ui";
import { MagneticScene, TIMELINE, hasWebGL } from "./MagneticScene";

gsap.registerPlugin(ScrollTrigger);
// mobile URL-bar show/hide must not re-measure the pin mid-scroll
ScrollTrigger.config({ ignoreMobileResize: true });

type Mode = "pending" | "webgl" | "fallback";

/** Two-line editorial headline: first word firmer, rest lighter; line 1 = first two words. */
function HeroTitle({ text }: { text: string }) {
  const words = text.trim().split(/\s+/);
  if (words.length < 3) return <h1 className="mo-hero-title">{text}</h1>;
  const [first, second, ...rest] = words;
  return (
    <h1 className="mo-hero-title" aria-label={text}>
      <span className="mo-line" aria-hidden>
        <span className="mo-key">{first}</span> <span className="mo-soft">{second}</span>
      </span>
      <span className="mo-line mo-soft" aria-hidden>{rest.join(" ")}</span>
    </h1>
  );
}

/** Hero with a real WebGL magnetic cube. Text stays real HTML above the canvas. */
export default function MagneticHero({ eyebrow, title, subtitle, primaryCta, secondaryCta, secondaryHref }: SectionProps) {
  const { images, ctaHref } = usePage();
  const wrapRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<Mode>("pending");
  const [reduced, setReduced] = useState(false);
  // transient GPU context loss rebuilds the scene instead of permanently dropping to the static photo
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const rm = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    setReduced(rm);
    if (!hasWebGL() || !stageRef.current) {
      setMode("fallback");
      return undefined;
    }
    let scene: MagneticScene | null = null;
    let retryTimer = 0;
    const onLost = () => {
      if (attempt < 3) retryTimer = window.setTimeout(() => setAttempt((n) => n + 1), 300);
      else setMode("fallback");
    };
    try {
      scene = new MagneticScene(stageRef.current, { still: rm, onLost });
    } catch {
      if (attempt < 3) {
        retryTimer = window.setTimeout(() => setAttempt((n) => n + 1), 300);
        return () => window.clearTimeout(retryTimer);
      }
      setMode("fallback");
      return undefined;
    }
    setMode("webgl");
    const content = contentRef.current;
    const scrim = scrimRef.current;
    const [a, b] = TIMELINE.textOut;
    const apply = (p: number) => {
      scene?.setProgress(p);
      const x = Math.min(1, Math.max(0, (p - a) / (b - a)));
      const k = x * x * (3 - 2 * x);
      if (content) {
        // text yields to the object: fully gone well before the cube locks in
        content.style.opacity = String(1 - k);
        content.style.transform = `translate3d(0, ${-28 * k}px, 0) scale(${1 - 0.05 * k})`;
        content.style.filter = k > 0.01 ? `blur(${(k * 6).toFixed(2)}px)` : "";
        content.style.visibility = k > 0.995 ? "hidden" : "";
        content.style.pointerEvents = k > 0.6 ? "none" : "";
      }
      if (scrim) scrim.style.opacity = String(1 - k);
    };
    let st: ScrollTrigger | null = null;
    if (!rm && wrapRef.current) {
      st = ScrollTrigger.create({
        trigger: wrapRef.current,
        start: "top top",
        end: "bottom bottom",
        invalidateOnRefresh: true,
        onUpdate: (self) => apply(self.progress),
        onRefresh: (self) => apply(self.progress),
      });
      apply(st.progress);
      requestAnimationFrame(() => ScrollTrigger.refresh());
    }
    return () => {
      window.clearTimeout(retryTimer);
      st?.kill();
      scene?.dispose();
      if (content) {
        content.style.opacity = "";
        content.style.transform = "";
        content.style.filter = "";
        content.style.visibility = "";
        content.style.pointerEvents = "";
      }
      if (scrim) scrim.style.opacity = "";
    };
  }, [attempt]);

  const pinned = mode !== "fallback" && !reduced;

  // Pin root-cause fix: the sticky nav sits in normal flow above the hero, so the stage used to scroll
  // ~70-80px before its sticky top:0 engaged. Pull the hero up underneath the nav so its top is the
  // document top: the stage is locked from scroll 0 and progress 0 == pin start.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const fit = () => {
      wrap.style.marginTop = "";
      const off = wrap.getBoundingClientRect().top + window.scrollY;
      wrap.style.marginTop = pinned && off > 0 ? `${-off}px` : "";
      ScrollTrigger.refresh();
    };
    fit();
    window.addEventListener("resize", fit);
    return () => {
      window.removeEventListener("resize", fit);
      wrap.style.marginTop = "";
    };
  }, [pinned]);

  return (
    <section ref={wrapRef} className={cn("mo-hero relative text-white", pinned && "h-[220vh] md:h-[290vh] lg:h-[360vh]")} aria-label={str(title)}>
      <div className="mo-pin sticky top-0 isolate h-[100lvh] w-full overflow-hidden">
        {mode === "fallback" ? (
          <>
            <div className="flash-hero-photo absolute inset-0 -z-20">
              <Slot img={images.hero} priority sizes="100vw" />
            </div>
            <div className="flash-hero-center-overlay absolute inset-0 -z-10" aria-hidden />
          </>
        ) : (
          <>
            <div className="mo-hero-haze absolute inset-0 -z-30" aria-hidden />
            <div ref={stageRef} className={cn("mo-stage pointer-events-none absolute inset-0 -z-20", mode === "webgl" && "is-ready")} aria-hidden />
            <div ref={scrimRef} className="mo-hero-scrim pointer-events-none absolute inset-0 -z-10" aria-hidden />
            <div className="mo-hero-overlay pointer-events-none absolute inset-0 -z-10" aria-hidden />
          </>
        )}
        <Container className="relative flex h-full flex-col items-center justify-center py-20 text-center md:py-28">
          <div ref={contentRef} className="flex w-full max-w-6xl flex-col items-center will-change-transform">
            {eyebrow ? <span className="mo-hero-kicker">{str(eyebrow)}</span> : null}
            <div className="mo-hero-text mt-6 md:mt-7">
              <HeroTitle text={str(title)} />
            </div>
            <p className="mo-hero-sub mt-6">{str(subtitle)}</p>
            <div className="mo-hero-ctas mt-9 flex flex-wrap justify-center gap-3">
              {primaryCta ? (
                <A to={ctaHref} className="sx-btn-solid h-11 px-6 text-[14px]">
                  {str(primaryCta)}
                </A>
              ) : null}
              {secondaryCta ? (
                <A to={str(secondaryHref) || ctaHref} className="sx-btn-outline h-11 px-6 text-[14px]">
                  {str(secondaryCta)}
                </A>
              ) : null}
            </div>
          </div>
        </Container>
      </div>
    </section>
  );
}